import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Goals: React.FC = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [goals, setGoals] = useState({
    dailyGoal: 0,
    weeklyGoal: 0,
    monthlyGoal: 0,
    annualGoal: 0,
    saturdayGoalPct: 40
  });
  const [inputs, setInputs] = useState({
    dailyGoal: '',
    weeklyGoal: '',
    monthlyGoal: '',
    annualGoal: '',
    saturdayGoalPct: '40'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(value || 0);

  const fetchGoal = async () => {
    try {
      const res = await axios.get('/api/settings/goals');
      const dailyGoal = Number(res.data?.dailyGoal || 0);
      const weeklyGoal = Number(res.data?.weeklyGoal || 0);
      const monthlyGoal = Number(res.data?.monthlyGoal || 0);
      const annualGoal = Number(res.data?.annualGoal || 0);
      const satPct = Number(res.data?.saturdayGoalPct ?? 40);
      const nextGoals = {
        dailyGoal: Number.isFinite(dailyGoal) ? dailyGoal : 0,
        weeklyGoal: Number.isFinite(weeklyGoal) ? weeklyGoal : 0,
        monthlyGoal: Number.isFinite(monthlyGoal) ? monthlyGoal : 0,
        annualGoal: Number.isFinite(annualGoal) ? annualGoal : 0,
        saturdayGoalPct: Number.isFinite(satPct) ? satPct : 40
      };
      setGoals(nextGoals);
      setInputs({
        dailyGoal: nextGoals.dailyGoal > 0 ? String(nextGoals.dailyGoal) : '',
        weeklyGoal: nextGoals.weeklyGoal > 0 ? String(nextGoals.weeklyGoal) : '',
        monthlyGoal: nextGoals.monthlyGoal > 0 ? String(nextGoals.monthlyGoal) : '',
        annualGoal: nextGoals.annualGoal > 0 ? String(nextGoals.annualGoal) : '',
        saturdayGoalPct: String(nextGoals.saturdayGoalPct)
      });
    } catch (error: any) {
      console.error('Error loading goals', error);
      toast.error(error.response?.data?.error || 'No se pudo cargar el objetivo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    fetchGoal();
  }, [isAdmin]);

  const saveGoal = async () => {
    const saturdayGoalPct = Number(inputs.saturdayGoalPct.replace(',', '.') || 0);
    const payload = {
      dailyGoal: Number(inputs.dailyGoal.replace(',', '.') || 0),
      weeklyGoal: Number(inputs.weeklyGoal.replace(',', '.') || 0),
      monthlyGoal: Number(inputs.monthlyGoal.replace(',', '.') || 0),
      annualGoal: Number(inputs.annualGoal.replace(',', '.') || 0),
      saturdayGoalPct
    };
    if ([payload.dailyGoal, payload.weeklyGoal, payload.monthlyGoal, payload.annualGoal].some((value) => !Number.isFinite(value) || value < 0)) {
      toast.error('Ingresa montos validos');
      return;
    }
    if (!Number.isFinite(saturdayGoalPct) || saturdayGoalPct < 0 || saturdayGoalPct > 100) {
      toast.error('El % del sábado debe estar entre 0 y 100');
      return;
    }
    try {
      setSaving(true);
      await axios.post('/api/settings/goals', payload);
      setGoals(payload);
      toast.success('Objetivos guardados');
    } catch (error: any) {
      console.error('Error saving goals', error);
      toast.error(error.response?.data?.error || 'No se pudieron guardar los objetivos');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-semibold text-foreground">Objetivos</h1>
        <p className="text-sm text-muted-foreground">No tenes permisos para acceder a esta seccion.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Objetivos</h1>
          <p className="text-sm text-muted-foreground">Define objetivos diarios, semanales, mensuales y anuales.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Objetivos de facturacion</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-sm text-muted-foreground">Cargando objetivo...</div>
          ) : (
            <>
              <div className="rounded-lg border bg-muted/40 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Actuales</p>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <p className="text-foreground">Diario: {goals.dailyGoal > 0 ? formatCurrency(goals.dailyGoal) : 'Sin objetivo'}</p>
                  <p className="text-foreground">Semanal: {goals.weeklyGoal > 0 ? formatCurrency(goals.weeklyGoal) : 'Sin objetivo'}</p>
                  <p className="text-foreground">Mensual: {goals.monthlyGoal > 0 ? formatCurrency(goals.monthlyGoal) : 'Sin objetivo'}</p>
                  <p className="text-foreground">Anual: {goals.annualGoal > 0 ? formatCurrency(goals.annualGoal) : 'Sin objetivo'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Objetivo diario (ARS)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={inputs.dailyGoal}
                    onChange={(e) => setInputs((prev) => ({ ...prev, dailyGoal: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Objetivo semanal (ARS)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={inputs.weeklyGoal}
                    onChange={(e) => setInputs((prev) => ({ ...prev, weeklyGoal: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Objetivo mensual (ARS)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={inputs.monthlyGoal}
                    onChange={(e) => setInputs((prev) => ({ ...prev, monthlyGoal: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Objetivo anual (ARS)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={inputs.annualGoal}
                    onChange={(e) => setInputs((prev) => ({ ...prev, annualGoal: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Objetivo del sábado (% del diario)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={100}
                    value={inputs.saturdayGoalPct}
                    onChange={(e) => setInputs((prev) => ({ ...prev, saturdayGoalPct: e.target.value }))}
                    placeholder="40"
                  />
                  <p className="text-xs text-muted-foreground">
                    Media jornada (9–12:30). Ej: 40% del objetivo diario. El domingo el objetivo es 0 (cerrado).
                  </p>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveGoal} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar objetivos'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Goals;
